import React, { useState, useEffect, useContext } from 'react';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { getSEOData } from 'cards/seo';
import { CARD_TYPES, SETTINGS_BY_CARD_TYPE } from 'cards/constants/CardTypes';
import { SEO_COLORS } from 'cards/constants/Cards';
import { ALL_PAPER_SHAPES } from 'cards/constants/CardLevelOptions';
import { getUserContext } from 'selectors/user/userSelectors';
import { getModuleGroup } from 'selectors/moduleGroupSelectors';
import {
  PLP_PROMO_MODULE_GROUP_TAGS_PRE_AUTH,
  PLP_PROMO_MODULE_GROUP_TAGS_POST_AUTH,
} from 'cards/components/CardListing/types';
import fetchModuleGroup from 'actions/ModuleGroupActions';
import { useResponsiveContext } from 'components/common/ResponsiveContext';
import { updateSearchFilters } from 'cards/actions/cardCatalogActions';
import { getSearchFilters } from 'cards/selectors/cardCatalogSelector';
import { getFullSeoFiltersFromUrl } from 'cards/components/CardListing/CardListingRedesign/utils';
import { SEOContentBlock } from 'cards/components/CardListing/SEOContentBlock';
import FeaturedArticlesCarousel from 'cards/components/common/FeaturedArticlesCarousel';
import { useIsPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';
import { getDesktopNavHeight } from 'components/common/header/helpers';
import { NAV_IDS_BY_CARD_TYPE } from 'cards/constants/NavIdsByCardType';
import CardListingWrapper from 'cards/components/CardListing/CardListingRedesign/CardListingWrapper';
import CardListingRedesign from 'cards/components/CardListing/CardListingRedesign/CardListingRedesign_next';
import { SEOHeader } from 'cards/components/CardListing/SEOHeader';

const LoadingHeightContext = React.createContext();

const UnifiedNav = dynamic(() => import('components/common/header/UnifiedNav'), {
  loading: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const value = useContext(LoadingHeightContext);
    return <div style={value} />;
  },
  ssr: false,
});

function CardListingPage({
  cardType,
  urlFilter,
  trackPageView,
  searchFilters,
  onUpdateSearchFilters,
  ssrSeoData,
  ssrSeoImage,
  activeSecondaryLink,
  activeCardEvent,
  allMotifs,
  isDigital,
  isPostAuthSSR,
  userContext,
  promoTag,
  promoModuleGroup,
  loadModuleGroup,
  articles,
}) {
  const [currentThemes, setCurrentThemes] = useState(getFullSeoFiltersFromUrl(urlFilter));
  const [seoData, setSeoData] = useState(ssrSeoData);
  const { isMobile, isDesktop } = useResponsiveContext();

  const isPostAuthNav3 = useIsPostAuthNav3MobileOrDesktop(userContext) || isPostAuthSSR;
  const { mobileTertiaryNavId, tertiaryNavId } = NAV_IDS_BY_CARD_TYPE[activeCardEvent];

  useEffect(() => {
    if (currentThemes.length > 0) {
      setCurrentThemes(currentThemes);
      onUpdateSearchFilters(
        'seo_colors',
        currentThemes.filter(e => SEO_COLORS.includes(e.toUpperCase()))
      );
      onUpdateSearchFilters(
        'motifs',
        currentThemes.filter(
          e => !SEO_COLORS.includes(e.toUpperCase()) && !ALL_PAPER_SHAPES.includes(e.toLowerCase())
        )
      );
      onUpdateSearchFilters(
        'silhouettes',
        currentThemes
          .filter(e => ALL_PAPER_SHAPES.includes(e.toLowerCase()))
          .map(e => e.toLowerCase())
      );
    }
  }, []);

  useEffect(() => {
    setSeoData(getSEOData(activeCardEvent, searchFilters, isDigital));
  }, [searchFilters, activeCardEvent, isDigital]);

  useEffect(() => {
    setCurrentThemes([
      ...searchFilters.motifs,
      ...searchFilters.seo_colors,
      ...searchFilters.silhouettes,
    ]);
  }, [searchFilters.motifs, searchFilters.silhouettes, searchFilters.seo_colors]);

  useEffect(() => {
    if (promoTag && !_isEmpty(userContext) && !promoModuleGroup) {
      loadModuleGroup(promoTag);
    }
  }, [loadModuleGroup, promoModuleGroup, promoTag, userContext]);

  const adjustedActiveSecondaryLink = isDigital
    ? SecondaryNavIdentifier.DIGITAL_SAVE_THE_DATE
    : activeSecondaryLink;

  let adjustedActiveTertiaryLink = !isDesktop ? mobileTertiaryNavId : tertiaryNavId;
  if (isDigital) {
    adjustedActiveTertiaryLink = TertiaryNavIdentifier.DIGITAL_SAVE_THE_DATE;
  }

  const navHeight = !isDesktop ? 60 : getDesktopNavHeight(false, userContext.is_guest);

  return (
    <React.Fragment>
      <SEOHeader seoData={seoData} imageUrl={ssrSeoImage} />
      <div className="row">
        <LoadingHeightContext.Provider value={{ height: `${navHeight}px` }}>
          <UnifiedNav
            secondaryType="invites"
            activePrimaryLink={PrimaryNavIdentifier.PAPER}
            activeSecondaryLink={
              isPostAuthNav3 ? SecondaryNavIdentifier.PAPER_SHOP : adjustedActiveSecondaryLink
            }
            activeTertiaryLink={adjustedActiveTertiaryLink}
            emitDirectionEvents
            enableSticky={!isMobile}
            isPostAuthSSR={isPostAuthSSR}
            userContext={userContext}
            initialHeight={navHeight}
          />
        </LoadingHeightContext.Provider>
      </div>
      <CardListingWrapper
        activeCardEvent={activeCardEvent}
        isMobile={isMobile}
        contentClassName={userContext.is_guest ? 'card-listing-wrapper-preauth' : ''}
      >
        <CardListingRedesign
          activeCardEvent={activeCardEvent}
          activeStyles={currentThemes}
          cardTypeUrl={cardType}
          isMobile={isMobile}
          isDesktop={isDesktop}
          trackPageView={trackPageView}
          allMotifs={allMotifs}
          isDigital={isDigital}
          promoTag={promoTag}
          seoData={seoData}
        />
        <FeaturedArticlesCarousel articles={articles} />
        <SEOContentBlock
          seoData={seoData}
          allMotifs={allMotifs}
          activeCardEvent={activeCardEvent}
          isDigital={isDigital}
        />
      </CardListingWrapper>
    </React.Fragment>
  );
}

const mapStateToProps = (state, props) => {
  const activeCardType = props.isDigital
    ? `DIGITAL_${props.activeCardEvent}`
    : props.activeCardEvent;
  const promoTag = state.user.userContext.is_guest
    ? PLP_PROMO_MODULE_GROUP_TAGS_PRE_AUTH[activeCardType]
    : PLP_PROMO_MODULE_GROUP_TAGS_POST_AUTH[activeCardType];

  return {
    searchFilters: getSearchFilters(state),
    userContext: getUserContext(state),
    promoModuleGroup: getModuleGroup(state, { tag: promoTag }),
    promoTag,
    allMotifs: state.cards.cardCatalog.motifs.filter(motif => {
      /*
       * The Disney motif does not applies to DSTD
       * nor WSI or TBN
       * BE will always return the full list of available motifs.
       * The only business logic on the BE for motifs is the distinction
       * between regular wedding based card types and Holidays.
       */
      return props.isDigital ||
        props.cardType === SETTINGS_BY_CARD_TYPE[CARD_TYPES.weddingShower].urlSlug ||
        props.cardType === SETTINGS_BY_CARD_TYPE[CARD_TYPES.tableNumber].urlSlug
        ? motif.key.toLowerCase() !== 'disney'
        : true;
    }),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateSearchFilters: (key, value) => dispatch(updateSearchFilters(key, value)),
  loadModuleGroup: tag => dispatch(fetchModuleGroup(tag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CardListingPage);
